import React, { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Navigate } from 'react-router-dom';
import { SEOHead } from '@/components/SEOHead';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Settings, Star, MapPin, Image, Bell } from 'lucide-react';
import { HomepageFeaturedManager } from '@/components/admin/HomepageFeaturedManager';
import { GalleryProjectsManager } from '@/components/admin/GalleryProjectsManager';
import { CityGroupsManager } from '@/components/admin/CityGroupsManager';
import { FeaturedProjectsManager } from '@/components/admin/FeaturedProjectsManager';
import { NotificationSettings } from '@/components/admin/NotificationSettings';

const AdminSettings = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen">Loading...</div>;
  }

  if (!user) {
    return <Navigate to="/admin/login" replace />;
  }

  return (
    <>
      <SEOHead 
        title="Admin - Settings"
        description="Manage site settings and configuration"
      />
      
      <div className="min-h-screen bg-background">
        <div className="container mx-auto p-6 space-y-6">
          {/* Header */}
          <div className="flex items-center gap-4">
            <Settings className="h-8 w-8" />
            <div>
              <h1 className="text-3xl font-bold">Settings</h1>
              <p className="text-muted-foreground">Manage site configuration and featured content</p>
            </div>
          </div>

          {/* Settings Tabs */}
          <Tabs defaultValue="notifications" className="space-y-6">
            <TabsList className="grid w-full grid-cols-5">
              <TabsTrigger value="notifications" className="flex items-center gap-2">
                <Bell className="h-4 w-4" />
                Notifications
              </TabsTrigger>
              <TabsTrigger value="featured" className="flex items-center gap-2">
                <Star className="h-4 w-4" />
                Featured
              </TabsTrigger>
              <TabsTrigger value="gallery" className="flex items-center gap-2">
                <Image className="h-4 w-4" />
                Gallery
              </TabsTrigger>
              <TabsTrigger value="cities" className="flex items-center gap-2">
                <MapPin className="h-4 w-4" />
                Cities
              </TabsTrigger>
              <TabsTrigger value="homepage" className="flex items-center gap-2">
                <Settings className="h-4 w-4" />
                Homepage
              </TabsTrigger>
            </TabsList>

            <TabsContent value="notifications" className="space-y-6">
              <NotificationSettings />
            </TabsContent>

            <TabsContent value="featured" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Star className="h-5 w-5" />
                    Featured Projects
                  </CardTitle>
                  <p className="text-sm text-muted-foreground">
                    Manage which projects are featured across the site
                  </p>
                </CardHeader>
                <CardContent>
                  <FeaturedProjectsManager />
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="gallery" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Image className="h-5 w-5" />
                    Gallery Projects
                  </CardTitle>
                  <p className="text-sm text-muted-foreground">
                    Configure which projects appear in the main gallery
                  </p>
                </CardHeader>
                <CardContent>
                  <GalleryProjectsManager />
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="cities" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <MapPin className="h-5 w-5" />
                    City Groups
                  </CardTitle>
                  <p className="text-sm text-muted-foreground">
                    Manage service areas and city groupings
                  </p>
                </CardHeader>
                <CardContent>
                  <CityGroupsManager />
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="homepage" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Settings className="h-5 w-5" />
                    Homepage Featured
                  </CardTitle>
                  <p className="text-sm text-muted-foreground">
                    Manage featured content displayed on the homepage
                  </p>
                </CardHeader>
                <CardContent>
                  <HomepageFeaturedManager />
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </>
  );
};

export default AdminSettings;